import { lazy, Suspense, useState, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "../redux/store";
import { updateAppointment } from "../redux/slices/appointmentSlice";


import { captureBooking } from "../utils";
import tinycolor from "tinycolor2";
import { formatPhoneNumber } from "../utils";
import { getGradientFromColor } from "../utils/theme";
import { BookingStage } from "../constants/bookingStages";

// The Google Maps wrapper is the largest dependency; load it only when the
// locations step renders (direct shop links never show it). If the chunk
// cannot be fetched, show Map's own load-error state instead of failing the
// whole step.
const Map = lazy(() =>
  import("../Map").catch(() => ({
    default: () => <div className="text-center p-4">Error loading maps</div>,
  }))
);

const extractFirstWord = (name) => {
  const match = name.match(/^[^\s]+/i);
  return match ? match[0].toLowerCase() : "";
};

const groupByFirstWord = (data, shop) => {
  if (!Array.isArray(data)) return undefined;

  const groupedData = data.reduce((acc, item) => {
    const firstWord = extractFirstWord(item.name);
    if (!acc[firstWord]) {
      acc[firstWord] = [];
    }
    acc[firstWord].push(item);
    return acc;
  }, {});

  return Object.values(groupedData).map((groupItems) => ({
    groupName: groupItems[0]?.name,
    items: groupItems,
  }));
};

const ShopLocations = ({ data, appointment }) => {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileView, setMobileView] = useState("list");
  const [isMapReady, setIsMapReady] = useState(false);
  const mapRef = useRef(null);
  const hasInitialMapFocusRef = useRef(false);
  const initialFocusShopIdRef = useRef(null);

  const dispatch = useDispatch();

  const groupedDataArray = useMemo(() => groupByFirstWord(data), [data]);
  const theme = groupedDataArray?.[0]?.items[0]?.features?.microsites?.bookings;

  const markers = useMemo(() => {
    const newMarkers = [];

    groupedDataArray?.forEach((group) => {
      group.items.forEach((item) => {
        if (item.features?.microsites?.bookings?.enabled) {
          const lat = Number(item?.business_info?.lat);
          const lng = Number(item?.business_info?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return;
          }

          const address = {
            item: item,
            address:
              item.name +
              ", " +
              item.business_info?.address1 +
              ", " +
              item.business_info?.city +
              ", " +
              item.business_info?.state +
              ", " +
              item.business_info?.zip,
            shopId: item._id,
            shopName: item.name,
            phoneNumber: item.business_info?.phone_number,
            sameDayBookings:
              item.features?.microsites?.bookings?.sameDayBookings,
            lat,
            lng,
          };

          newMarkers.push({
            position: { lat: address?.lat, lng: address?.lng },
            title: address,
            id: newMarkers.length,
            shopId: address.shopId,
            sameDayBookings: address.sameDayBookings,
            phoneNumber: address.phoneNumber,
            shopData: address.item,
          });
        }
      });
    });

    return newMarkers.sort((a, b) =>
      a.title?.shopName.localeCompare(b.title?.shopName)
    );
  }, [groupedDataArray]);

  useEffect(() => {
    if (appointment?.bookingId && appointment?.stage === BookingStage.Location) {
      let resetService = { ...appointment, service: "" };
      captureBooking(resetService);

      dispatch(
        updateAppointment({
          updates: {
            service: "",
          },
        })
      );
    }
  }, []);

  const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const update = () => {
      const isMobileViewport = media.matches;
      setIsMobile(isMobileViewport);
      if (!isMobileViewport) {
        setMobileView("list");
      }
    };
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const showList = !isMobile || mobileView === "list";
  const isMapVisible = !isMobile || mobileView === "map";

  useEffect(() => {
    if (!isMapVisible || !isMapReady || !mapRef.current || !window.google?.maps) {
      return;
    }

    const renderTimeout = window.setTimeout(() => {
      window.google.maps.event.trigger(mapRef.current, "resize");

      if (selectedMarker?.position) {
        mapRef.current.panTo(selectedMarker.position);
      }
    }, 120);

    return () => window.clearTimeout(renderTimeout);
  }, [isMapVisible, isMapReady, selectedMarker]);

  useEffect(() => {
    const firstShopId = markers[0]?.shopId || null;
    if (firstShopId !== initialFocusShopIdRef.current) {
      initialFocusShopIdRef.current = firstShopId;
      hasInitialMapFocusRef.current = false;
    }

    if (
      !isMapReady ||
      !mapRef.current ||
      hasInitialMapFocusRef.current ||
      markers.length === 0
    ) {
      return;
    }

    const firstMarker = markers[0];
    mapRef.current.panTo(firstMarker.position);
    mapRef.current.setZoom(9);
    hasInitialMapFocusRef.current = true;
  }, [isMapReady, markers]);

  useEffect(() => {
    if (!selectedMarker) return;

    const markerStillExists = markers.some(
      (marker) => marker.shopId === selectedMarker.shopId
    );

    if (!markerStillExists) {
      setSelectedMarker(null);
    }
  }, [markers, selectedMarker]);

  const focusMarker = (marker, options = {}) => {
    const { shouldSetZoom = true } = options;

    const markerLat = Number(marker?.position?.lat);
    const markerLng = Number(marker?.position?.lng);
    if (!Number.isFinite(markerLat) || !Number.isFinite(markerLng)) {
      return;
    }

    if (mapRef?.current) {
      mapRef.current.panTo({ lat: markerLat, lng: markerLng });

      if (shouldSetZoom) {
        const currentZoom = mapRef.current.getZoom?.();
        if (typeof currentZoom !== "number" || currentZoom < 9) {
          mapRef.current.setZoom(9);
        }
      }
    }

    setSelectedMarker(marker);
  };

  const handleMouseOver = (marker) => {
    if (!isTouchDevice) {
      focusMarker(marker, { shouldSetZoom: true });
    }
  };

  const handleMarkerClick = (marker) => {
    focusMarker(marker, { shouldSetZoom: !isMobile });
  };

  const handleInfoWindowClose = () => {
    setSelectedMarker(null);
  };

  const handleMapApiLoad = () => {
    setIsMapReady(true);
  };

  const neutralAccent = "#2b73b9";
  const { accentDark: neutralAccentDark, gradientBackground } = useMemo(
    () => getGradientFromColor(neutralAccent),
    []
  );
  const toggleBase = neutralAccent;
  const toggleActive = neutralAccentDark;
  const buttonTextColor = "#ffffff";

  const handleSelectShop = (marker) => {
    const selectedShopData = { ...marker.shopData, shopId: marker.shopId };
    dispatch(
      updateAppointment({
        updates: {
          domain: appointment.domain,
          shopId: marker.shopId,
          shopData: selectedShopData,
          stage: appointment.stage + 1,
        },
      })
    );
  };

  const phoneCtaClassName =
    "box-border block w-full cursor-pointer rounded-lg border-2 border-solid px-3 py-2 text-center text-xs font-semibold transition-opacity hover:opacity-80 active:opacity-70";
  const bookCtaClassName =
    "w-full cursor-pointer rounded-lg px-3 py-2 text-center text-xs font-semibold transition-opacity hover:opacity-90 active:opacity-80";
  const shopAddressClassName =
    "mt-1 text-sm leading-snug text-slate-500 md:text-xs";
  const phoneCtaStyle = {
    borderColor: neutralAccent,
    color: neutralAccent,
    backgroundColor: "#ffffff",
    textDecoration: "none",
    WebkitAppearance: "none",
    appearance: "none",
    outline: "none",
    boxShadow: "none",
  };
  const bookCtaStyle = {
    backgroundColor: neutralAccentDark,
    color: buttonTextColor,
    WebkitAppearance: "none",
    appearance: "none",
    border: "0",
    outline: "none",
    boxShadow: "none",
  };

  const getFormattedPhone = (marker) =>
    marker?.phoneNumber
      ? formatPhoneNumber(marker.phoneNumber) || marker.phoneNumber
      : null;

  const renderPhoneCta = (marker) => {
    const formattedPhone = getFormattedPhone(marker);
    if (!formattedPhone) return null;

    return (
      <a
        href={`tel:${marker.phoneNumber.replace(/\D/g, "")}`}
        target="_top"
        className={phoneCtaClassName}
        style={phoneCtaStyle}
        onClick={(event) => {
          event.stopPropagation();
          if (window.parent !== window) {
            event.preventDefault();
            window.parent.postMessage(
              { type: "DC_TEL_LINK", tel: `tel:${marker.phoneNumber.replace(/\D/g, "")}` },
              "*"
            );
          }
        }}
      >
        {formattedPhone}
      </a>
    );
  };

  const renderBookCta = (marker) => (
    <button
      type="button"
      className={bookCtaClassName}
      style={bookCtaStyle}
      onClick={(event) => {
        event.stopPropagation();
        handleSelectShop(marker);
      }}
    >
      Book Appointment
    </button>
  );

  const renderInfoWindowContent = (marker) => {
    if (!marker) return null;

    const [, ...addressParts] =
      marker?.title?.address?.split(",") || [];

    const streetAddress = addressParts?.[0]?.trim();
    const city = addressParts?.[1]?.trim();
    const state = addressParts?.[2]?.trim();
    const zip = addressParts?.[3]?.trim();
    const cityStateZip = [city, [state, zip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", ");

    return (
      <div className="w-[220px] text-slate-700">
        {(streetAddress || cityStateZip) && (
          <div className={shopAddressClassName}>
            {streetAddress && <div>{streetAddress}</div>}
            {cityStateZip && <div>{cityStateZip}</div>}
          </div>
        )}
        <div className="mt-3 grid gap-2">
          {renderPhoneCta(marker)}
          {renderBookCta(marker)}
        </div>
      </div>
    );
  };

  const getInfoWindowOptions = (marker) => {
    const shopName = marker?.title?.shopName || "";

    if (typeof document === "undefined") {
      return { headerContent: shopName };
    }

    const headerContent = document.createElement("div");
    headerContent.textContent = shopName;
    headerContent.style.fontWeight = "700";
    headerContent.style.fontSize = "16px";
    headerContent.style.lineHeight = "1.2";
    headerContent.style.color = "#1e293b";

    return { headerContent };
  };

  return (
    <div className="h-full min-h-0 w-full font-roboto">
      <div className="flex h-full min-h-0 flex-col md:min-h-[520px] md:flex-row">
        {showList && (
          <div
            className="relative md:w-[280px] md:min-h-[520px]"
            style={{ background: gradientBackground }}
          >
            <div className="md:max-h-[520px] overflow-y-auto px-4 py-4 md:px-5 md:py-5">
              {markers.map((marker, index) => {
                const [businessName, ...addressParts] =
                  marker.title.address.split(",");

                return (
                  <div
                    key={marker.shopId || index}
                    id={`dc-shop-${businessName.replace(/\s+/g, "-")}`}
                    className="mb-3 rounded-[16px] bg-white p-3 shadow-sm transition-shadow"
                    data-testid="shop-card"
                    data-shop-id={marker.shopId}
                    style={{
                      boxShadow:
                        theme?.shadow === "shadow-md"
                          ? `0 6px 12px ${neutralAccentDark}33`
                          : "none",
                    }}
                    onMouseOver={() => handleMouseOver(marker)}
                  >
                    <div className="flex gap-3">
                      <div className="mt-1">
                        <i
                          className="fa-solid fa-location-dot"
                          aria-hidden="true"
                          style={{
                            color: neutralAccent,
                            fontSize: "22px",
                            lineHeight: 1,
                          }}
                        />
                      </div>
                      <div className="flex-1 text-slate-700">
                        <div className="text-sm font-semibold leading-tight">
                          {businessName}
                        </div>
                        {addressParts.length > 0 && (
                          <div className={shopAddressClassName}>
                            <div>{addressParts[0]}</div>
                            <div>
                              {addressParts[1]}, {addressParts[2]}{" "}
                              {addressParts[3]}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2">
                      {renderPhoneCta(marker)}
                      {renderBookCta(marker)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div
          className={`flex-1 min-h-0 md:min-h-[520px] ${
            isMobile && !isMapVisible ? "hidden" : "block"
          }`}
        >
          <Suspense
            fallback={<div className="text-center p-4">Loading maps...</div>}
          >
            <Map
              markers={markers}
              handleApiLoad={handleMapApiLoad}
              mapRef={mapRef}
              selectedMarker={selectedMarker}
              onMarkerClick={handleMarkerClick}
              onInfoWindowClose={handleInfoWindowClose}
              renderInfoWindowContent={renderInfoWindowContent}
              getInfoWindowOptions={getInfoWindowOptions}
              containerStyle={{
                height: "100%",
                width: "100%",
                borderRadius: "0px",
              }}
            />
          </Suspense>
        </div>
      </div>

      {isMobile && (
        <div className="fixed bottom-20 right-6 z-20">
          <div
            className="relative grid h-[52px] w-[136px] grid-cols-2 overflow-hidden rounded-full"
            style={{ backgroundColor: toggleBase }}
            role="tablist"
            aria-label="Location view toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mobileView === "list"}
              aria-label="Show list view"
              onClick={() => setMobileView("list")}
              className="relative z-10 flex h-full w-full items-center justify-center bg-transparent p-0 outline-none"
              style={{
                appearance: "none",
                WebkitAppearance: "none",
                border: "0",
                backgroundColor: mobileView === "list"
                  ? toggleActive
                  : "transparent",
                borderRadius: "9999px 0 0 9999px",
                padding: 0,
                margin: 0,
              }}
            >
              <i
                className="fa-solid fa-bars"
                aria-hidden="true"
                style={{
                  color: tinycolor("#ffffff")
                    .setAlpha(mobileView === "list" ? 1 : 0.9)
                    .toRgbString(),
                  fontSize: "21px",
                  lineHeight: 1,
                }}
              />
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={mobileView === "map"}
              aria-label="Show map view"
              onClick={() => setMobileView("map")}
              className="relative z-10 flex h-full w-full items-center justify-center bg-transparent p-0 outline-none"
              style={{
                appearance: "none",
                WebkitAppearance: "none",
                border: "0",
                backgroundColor: mobileView === "map"
                  ? toggleActive
                  : "transparent",
                borderRadius: "0 9999px 9999px 0",
                padding: 0,
                margin: 0,
              }}
            >
              <i
                className="fa-solid fa-location-dot"
                aria-hidden="true"
                style={{
                  color: tinycolor("#ffffff")
                    .setAlpha(mobileView === "map" ? 1 : 0.9)
                    .toRgbString(),
                  fontSize: "21px",
                  lineHeight: 1,
                }}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export { ShopLocations };
